import { Direction, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread as UnifiedThread, ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { getThreadItemCreatedAt, getThreadItemId, isSmsMessageItem } from '../../../utils/thread-items-utils';

export const upsertThreadItemOntoUnifiedThread = (
  unifiedThread: UnifiedThread,
  threadItem: ThreadItem,
  messageLimit = 1
): UnifiedThread => {
  if (!isSmsMessageItem(threadItem)) return unifiedThread;

  const items = unifiedThread.items ?? [];

  const originalLatestThreadItem = items[0];
  if (!originalLatestThreadItem) {
    return {
      ...unifiedThread,
      items: [threadItem],
    };
  }

  // Check if the item already exists
  const hasMatchingItem = items.some((existing) => getThreadItemId(existing) === getThreadItemId(threadItem));
  let hasUpsertedItem = false;

  // Insert or replace, maintaining descending order by createdAt
  const newlyOrderedItems = items.reduce<ThreadItem[]>((acc, curr) => {
    if (hasMatchingItem) {
      if (getThreadItemId(curr) === getThreadItemId(threadItem)) {
        acc.push(threadItem);
        hasUpsertedItem = true;
      } else {
        acc.push(curr);
      }
      return acc;
    }

    if (new Date(getThreadItemCreatedAt(threadItem)).getTime() > new Date(getThreadItemCreatedAt(curr)).getTime()) {
      acc.push(threadItem);
      hasUpsertedItem = true;
    }
    acc.push(curr);
    return acc;
  }, []);

  if (!hasUpsertedItem) newlyOrderedItems.push(threadItem);

  // Limit the number of items if needed
  const limitedNewItems =
    messageLimit >= newlyOrderedItems.length ? newlyOrderedItems : newlyOrderedItems.slice(0, messageLimit);

  const newLatestThreadItem = limitedNewItems.find((limitedNewItem) => isSmsMessageItem(limitedNewItem));

  if (!newLatestThreadItem) {
    return {
      ...unifiedThread,
      items: limitedNewItems,
    };
  }

  const isDifferentSms = getThreadItemId(originalLatestThreadItem) !== getThreadItemId(newLatestThreadItem);
  const originalIsOlder =
    getThreadItemCreatedAt(originalLatestThreadItem) < getThreadItemCreatedAt(newLatestThreadItem);
  let newStatus = unifiedThread.status;
  if (newLatestThreadItem.smsMessage.status === Status.STATUS_ERROR) {
    newStatus = ThreadReadStatus.THREAD_READ_STATUS_ERROR;
  } else if (
    newLatestThreadItem.smsMessage.direction === Direction.DIRECTION_INBOUND &&
    newLatestThreadItem.smsMessage.status !== Status.STATUS_READ
  ) {
    newStatus = ThreadReadStatus.THREAD_READ_STATUS_NEW;
  } else if (!newLatestThreadItem.smsMessage.autogeneratedBy) {
    newStatus = ThreadReadStatus.THREAD_READ_STATUS_READ;
  }

  if (!isDifferentSms || originalIsOlder) {
    return {
      ...unifiedThread,
      items: limitedNewItems,
      status: newStatus,
      actionable:
        isDifferentSms &&
        newLatestThreadItem.smsMessage.direction === Direction.DIRECTION_OUTBOUND &&
        !newLatestThreadItem.smsMessage.autogeneratedBy
          ? false
          : unifiedThread.actionable,
    };
  }

  return { ...unifiedThread, items: limitedNewItems };
};
